import { useState, useEffect, useCallback } from 'react';
import { User, Message, Conversation, Notification } from '@/types/chat';
import { mockUsers, mockMessages, mockConversations, mockNotifications } from '@/lib/mockData';

const STORAGE_KEYS = {
  CURRENT_USER: 'chat_current_user',
  MESSAGES: 'chat_messages',
  CONVERSATIONS: 'chat_conversations',
  NOTIFICATIONS: 'chat_notifications'
};

// BroadcastChannel for real-time cross-tab sync
const chatChannel = new BroadcastChannel('chat-sync');

export const useChatStore = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    return stored ? JSON.parse(stored) : mockUsers[0];
  });

  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.MESSAGES);
    return stored ? JSON.parse(stored).map((m: any) => ({
      ...m,
      timestamp: new Date(m.timestamp)
    })) : mockMessages;
  });

  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const arr = Array.isArray(parsed) ? parsed : [];
        if (arr.length === 0) {
          return mockConversations;
        }
        return arr.map((c: any) => ({
          ...c,
          weddingDate: new Date(c.weddingDate),
          createdAt: new Date(c.createdAt),
          lastMessageAt: new Date(c.lastMessageAt)
        }));
      } catch (e) {
        console.warn('Failed to parse conversations from storage, using mocks');
        return mockConversations;
      }
    }
    return mockConversations;
  });

  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS);
    return stored ? JSON.parse(stored).map((n: any) => ({
      ...n,
      timestamp: new Date(n.timestamp)
    })) : mockNotifications;
  });

  // Sync data to localStorage and notify other listeners (tabs and same page)
  const syncToOtherTabs = useCallback((key: string, data: any) => {
    localStorage.setItem(key, JSON.stringify(data));
    chatChannel.postMessage({ key, data });
    // Fallback for same-tab multi-instance sync
    window.dispatchEvent(new CustomEvent('chat-sync', { detail: { key, data } }));
  }, []);

  // Don't sync current user across tabs to allow independent demo sessions
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(currentUser));
  }, [currentUser]);

  useEffect(() => {
    syncToOtherTabs(STORAGE_KEYS.MESSAGES, messages);
  }, [messages, syncToOtherTabs]);

  useEffect(() => {
    syncToOtherTabs(STORAGE_KEYS.CONVERSATIONS, conversations);
  }, [conversations, syncToOtherTabs]);

  useEffect(() => {
    syncToOtherTabs(STORAGE_KEYS.NOTIFICATIONS, notifications);
  }, [notifications, syncToOtherTabs]);

  // Listen for updates from other tabs via BroadcastChannel and storage events
  useEffect(() => {
    const applyPayload = (key: string, data: any) => {
      if (key === STORAGE_KEYS.MESSAGES) {
        setMessages(data.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp)
        })));
      }
      if (key === STORAGE_KEYS.CONVERSATIONS) {
        setConversations(data.map((c: any) => ({
          ...c,
          weddingDate: new Date(c.weddingDate),
          createdAt: new Date(c.createdAt),
          lastMessageAt: new Date(c.lastMessageAt)
        })));
      }
      if (key === STORAGE_KEYS.NOTIFICATIONS) {
        setNotifications(data.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        })));
      }
    };

    const handleBroadcast = (event: MessageEvent) => {
      const { key, data } = event.data;
      applyPayload(key, data);
    };

    const handleWindowSync = (event: Event) => {
      const { key, data } = (event as CustomEvent).detail || {};
      if (!key) return;
      applyPayload(key, data);
    };

    const handleStorage = (e: StorageEvent) => {
      if (!e.newValue) return;
      const key = e.key!;
      const data = JSON.parse(e.newValue);
      applyPayload(key, data);
    };

    chatChannel.addEventListener('message', handleBroadcast);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('chat-sync', handleWindowSync as EventListener);

    return () => {
      chatChannel.removeEventListener('message', handleBroadcast);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('chat-sync', handleWindowSync as EventListener);
    };
  }, []);

  const switchUser = (userId: string) => {
    const user = mockUsers.find(u => u.id === userId);
    if (user) {
      setCurrentUser(user);
      // Reload the page to ensure role-specific views load correctly
      setTimeout(() => window.location.reload(), 100);
    }
  };

  const sendMessage = (conversationId: string, content: string, type: 'text' | 'image' | 'pdf' = 'text', attachmentUrl?: string, attachmentName?: string) => {
    if (!currentUser) return;

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      conversationId,
      senderId: currentUser.id,
      content,
      type,
      timestamp: new Date(),
      read: false,
      attachmentUrl,
      attachmentName
    };

    setMessages(prev => {
      const next = [...prev, newMessage];
      console.log('[sendMessage] appended', { conversationId, count: next.length });
      return next;
    });

    // Update conversation last message time
    setConversations(prev => prev.map(c => 
      c.id === conversationId 
        ? { ...c, lastMessageAt: new Date() }
        : c
    ));

    // Create notification for recipient
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      const recipientId = currentUser.id === conversation.clientId 
        ? conversation.talentId 
        : conversation.clientId;
      
      const newNotification: Notification = {
        id: `notif-${Date.now()}`,
        userId: recipientId,
        message: `New message from ${currentUser.name}`,
        timestamp: new Date(),
        read: false,
        conversationId
      };

      setNotifications(prev => [...prev, newNotification]);
    }
  };

  const markAsRead = (messageId: string) => {
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, read: true } : m
    ));
  };

  const markNotificationAsRead = (notificationId: string) => {
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
  };

  const isConversationVisible = (conversation: Conversation): boolean => {
    // For demo purposes, show all conversations
    return true;
  };

  const getVisibleConversations = () => {
    // Demo: show all conversations to every role
    return conversations;
  };

  const getUserNotifications = () => {
    if (!currentUser) return [];
    return notifications.filter(n => n.userId === currentUser.id);
  };

  const createConversation = (otherUserId: string, weddingDate: Date) => {
    if (!currentUser) return null;

    const otherUser = mockUsers.find(u => u.id === otherUserId);
    if (!otherUser) return null;

    // Determine client and talent IDs based on roles (demo-safe)
    let clientId: string | null = null;
    let talentId: string | null = null;

    if (currentUser.role === 'client') {
      clientId = currentUser.id;
      talentId = otherUserId; // can be talent or admin for demo
    } else if (currentUser.role === 'talent') {
      clientId = otherUserId; // other must be client/admin for demo
      talentId = currentUser.id;
    } else if (currentUser.role === 'admin') {
      if (otherUser.role === 'client') {
        clientId = otherUser.id;
        talentId = (mockUsers.find(u => u.role === 'talent')?.id) || otherUser.id;
      } else if (otherUser.role === 'talent') {
        talentId = otherUser.id;
        clientId = (mockUsers.find(u => u.role === 'client')?.id) || otherUser.id;
      } else {
        // Fallback: pair with first client and first talent if available
        clientId = (mockUsers.find(u => u.role === 'client')?.id) || otherUser.id;
        talentId = (mockUsers.find(u => u.role === 'talent')?.id) || otherUser.id;
      }
    } else {
      // Other roles (billing/sales/editing): treat as spectator, pair selected user with a counterpart
      if (otherUser.role === 'client') {
        clientId = otherUser.id;
        talentId = (mockUsers.find(u => u.role === 'talent')?.id) || otherUser.id;
      } else if (otherUser.role === 'talent') {
        talentId = otherUser.id;
        clientId = (mockUsers.find(u => u.role === 'client')?.id) || otherUser.id;
      } else {
        clientId = (mockUsers.find(u => u.role === 'client')?.id) || otherUser.id;
        talentId = (mockUsers.find(u => u.role === 'talent')?.id) || otherUser.id;
      }
    }

    if (!clientId || !talentId) return null;

    // Ensure client and talent aren't identical
    if (clientId === talentId) {
      const fallbackClient = mockUsers.find(u => u.role === 'client' && u.id !== talentId)?.id;
      const fallbackTalent = mockUsers.find(u => u.role === 'talent' && u.id !== clientId)?.id;
      if (otherUser.role === 'client' && fallbackTalent) talentId = fallbackTalent;
      else if (otherUser.role === 'talent' && fallbackClient) clientId = fallbackClient;
    }

    console.log('[createConversation]', { currentUser, otherUser, clientId, talentId, weddingDate });

    // Check for existing conversation between these two
    const existingConversation = conversations.find(c =>
      (c.clientId === clientId && c.talentId === talentId) ||
      (c.clientId === talentId && c.talentId === clientId)
    );

    if (existingConversation) return existingConversation;

    const newConversation: Conversation = {
      id: `conv-${Date.now()}`,
      clientId,
      talentId,
      weddingDate,
      createdAt: new Date(),
      lastMessageAt: new Date()
    };

    setConversations(prev => {
      const next = [...prev, newConversation];
      console.log('[createConversation] conversations length ->', next.length);
      return next;
    });
    return newConversation;
  };

  return {
    currentUser,
    switchUser,
    messages,
    conversations,
    notifications,
    sendMessage,
    markAsRead,
    markNotificationAsRead,
    isConversationVisible,
    getVisibleConversations,
    getUserNotifications,
    createConversation,
    mockUsers
  };
};
